import { createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { OptionType } from '@shared/ui/MultiSelect';

import {
    editInfo,
    addCustomer,
    editCustomer,
    createManager,
    editManager,
    deleteManager,
    getDashboard,
    deleteCustomer,
    uploadVisualization,
} from './action';
import { DashboardSchema, UpdateInformationAboutObjectRequest } from './types/dashboardSchema';
import {
    CustomerInfo,
    Weather,
    Visualization,
    ConstructionProgress,
    FinancialIndicators,
    ProjectUser,
    SortedUser,
    ContactPerson,
    DashboardSortedUser,
} from '../../../DashboardInformation/types/dashboardInformation';

const visualizationAdapter = createEntityAdapter<Visualization>();
const customerAdapter = createEntityAdapter<CustomerInfo>();
const contactPersonsAdapter = createEntityAdapter<ContactPerson>();
const progressAdapter = createEntityAdapter<ConstructionProgress>();
const projectManagerAdapter = createEntityAdapter<ProjectUser>();
const projectForemanAdapter = createEntityAdapter<ProjectUser>();
const projectPurchasesAdapter = createEntityAdapter<ProjectUser>();

const initialCustomerState = customerAdapter.getInitialState();

const initialProjectManagerState = projectManagerAdapter.getInitialState();
const initialProjectForemanState = projectForemanAdapter.getInitialState();
const initialProjectPurchasesState = projectPurchasesAdapter.getInitialState();
const initialContactPersonsState = contactPersonsAdapter.getInitialState();
const initialVisualizationState = visualizationAdapter.getInitialState();
const initialProgressState = progressAdapter.getInitialState();

const initialFinancialIndicators: FinancialIndicators = {
    current_period_customer_expenses: 0,
    current_period_expenses: 0,
    upcoming_period_customer_expenses: 0,
    upcoming_period_expenses: 0,
    currency: {
        id: 0,
        title: '',
        sign: '',
        code: '',
        short_code: '',
    },
    key_metrics: {
        customer_debt: 0,
        budget_saving: 0,
        project_balance: 0,
        profit_forecast_value: 0,
        profit_forecast_percent: 0,
        planned_profit_value: 0,
        planned_profit_percent: 0,
    },
    current_period_customer_expenses_percent: 0,
    current_period_expenses_percent: 0,
    upcoming_period_customer_expenses_percent: 0,
    upcoming_period_expenses_percent: 0,
};

const initialState: DashboardSchema = {
    projectId: null,
    needFetchData: true,
    editLoader: false,
    can_export_financial_info: true,
    can_show_financial_info: true,
    is_can_archive_project: false,
    is_customer: false,
    is_owner: false,
    progress_tariff_access_edit: false,
    modalMode: null,
    modalLoader: false,
    customerModalLoader: false,
    error: null,
    isLoading: true,
    isEditedManager: false,
    currentCustomerId: null,
    currentManagerId: null,
    currentProgressId: null,
    albums: [],
    temporalAlbums: [],
    selectedAlbums: [],
    tabs: {
        show: true,
        data: {},
    },
    information: {
        name: '',
        address: '',
        contract_number: '',
        contract_date: '',
        description: '',
        is_archive: false,
        latitude: 0,
        longitude: 0,
        customers: initialCustomerState,
        legal_entities: [],
        contactPersons: initialContactPersonsState,
        projectUsers: {
            sorted_users: {
                rest_of_team_count: 0,
                users: [],
            },
            project_users: {
                manager: initialProjectManagerState,
                foreman: initialProjectForemanState,
                purchase_agent: initialProjectPurchasesState,
            },
        },
    },
    weather: {
        current_date: '',
        current_temperature: '',
        current_icon_image_url: '',
        latitude: 0,
        longitude: 0,
        days: [],
        map_url: '',
    },
    constructionProgress: initialProgressState,
    documents: {
        currentDocument: null,
        is_having_contract_document: false,
        is_having_proposal_document: false,
        tariff_access: {
            doc_act: false,
            doc_contract: false,
            doc_kp: false,
            doc_order: false,
            doc_reconciliation: false,
            doc_your_contract: false,
            doc_your_proposal: false,
        },
    },
    estimates: {
        in_draft_estimates_cost: 0,
        in_draft_estimates_count: 0,
        in_exploitations_estimates_cost: 0,
        in_exploitations_estimates_count: 0,
        in_exploitations_estimates_done_percent: 0,
        estimates: [],
        currency: {
            id: 0,
            title: '',
            sign: '',
            code: '',
            short_code: '',
        },
    },
    visualization: initialVisualizationState,
    financial_info: initialFinancialIndicators,
};

export const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {
        setProjectId: (state, { payload }: PayloadAction<number>) => {
            state.projectId = payload;
        },
        updateWeather: (state, action: PayloadAction<Weather>) => {
            state.weather = action.payload;
        },
        setSelectedAlbums: (state, { payload }: PayloadAction<string[]>) => {
            state.selectedAlbums = payload;
        },
        onChangeSelectedAlbums: (state, { payload }: PayloadAction<string | undefined>) => {
            let updatedSelectedAlbums: string[] = [];

            if (payload) {
                const isIncludedAlbum = state.selectedAlbums.includes(payload);

                updatedSelectedAlbums = isIncludedAlbum ? [] : [payload];
            }

            state.selectedAlbums = updatedSelectedAlbums;
        },
        setCustomers: (state, action: PayloadAction<CustomerInfo[]>) => {
            customerAdapter.setMany(state.information.customers, action.payload);
        },
        setProgress: (state, action: PayloadAction<ConstructionProgress[]>) => {
            progressAdapter.setMany(state.constructionProgress, action.payload);
        },
        setProgressTariffAccessEdit: (state, action: PayloadAction<boolean>) => {
            state.progress_tariff_access_edit = action.payload;
        },
        addProgress: (state, action: PayloadAction<ConstructionProgress[]>) => {
            progressAdapter.setAll(state.constructionProgress, action.payload);
        },
        setAlbums: (state, { payload }: PayloadAction<OptionType[]>) => {
            state.albums = payload;
            state.temporalAlbums = [];
            const albumsIds = payload.map((album) => album.value);
            state.selectedAlbums = state.selectedAlbums.filter((al) => albumsIds.includes(al));
        },
        addAlbums: (state, { payload }: PayloadAction<OptionType>) => {
            state.temporalAlbums = [...state.temporalAlbums, payload];
        },
        setManagers: (state, action: PayloadAction<ContactPerson[]>) => {
            contactPersonsAdapter.setMany(state.information.contactPersons, action.payload);
        },
        setManager: (state, action: PayloadAction<ProjectUser[]>) => {
            projectManagerAdapter.setAll(
                state.information.projectUsers.project_users.manager,
                action.payload
            );
        },
        addProjectManager: (state, action: PayloadAction<ProjectUser>) => {
            const newId = action.payload.id;

            state.information.projectUsers.project_users.manager.ids.unshift(newId);
            state.information.projectUsers.project_users.manager.entities[newId] = action.payload;
        },
        removeProjectManager: (state, action: PayloadAction<string>) => {
            projectManagerAdapter.removeAll(state.information.projectUsers.project_users.manager);
        },
        setForeman: (state, action: PayloadAction<ProjectUser[]>) => {
            projectForemanAdapter.setAll(
                state.information.projectUsers.project_users.foreman,
                action.payload
            );
        },
        addProjectForeman: (state, action: PayloadAction<ProjectUser>) => {
            const newId = action.payload.id;

            state.information.projectUsers.project_users.foreman.ids.unshift(newId);
            state.information.projectUsers.project_users.foreman.entities[newId] = action.payload;
        },
        removeProjectForeman: (state, action: PayloadAction<string>) => {
            projectForemanAdapter.removeAll(state.information.projectUsers.project_users.foreman);
        },
        setPurchases: (state, action: PayloadAction<ProjectUser[]>) => {
            projectPurchasesAdapter.setAll(
                state.information.projectUsers.project_users.purchase_agent,
                action.payload
            );
        },
        setSortedUsers: (state, action: PayloadAction<DashboardSortedUser>) => {
            state.information.projectUsers.sorted_users = { ...action.payload };
        },
        addProjectPurchases: (state, action: PayloadAction<ProjectUser>) => {
            const newId = action.payload.id;

            state.information.projectUsers.project_users.purchase_agent.ids.unshift(newId);
            state.information.projectUsers.project_users.purchase_agent.entities[newId] =
                action.payload;
        },
        removeProjectPurchases: (state, action: PayloadAction<string>) => {
            projectPurchasesAdapter.removeAll(
                state.information.projectUsers.project_users.purchase_agent
            );
        },
        setVisualization: (state, action: PayloadAction<Visualization[]>) => {
            visualizationAdapter.setMany(state.visualization, action.payload);
        },
        addCustomer: (state, action: PayloadAction<CustomerInfo>) => {
            customerAdapter.addOne(state.information.customers, action.payload);
        },
        addManager: (state, action: PayloadAction<ContactPerson>) => {
            contactPersonsAdapter.addOne(state.information.contactPersons, action.payload);
        },
        updateManager: (state, action: PayloadAction<ContactPerson>) => {
            contactPersonsAdapter.updateOne(state.information.contactPersons, {
                id: action.payload.id,
                changes: action.payload,
            });
        },
        updateCustomer: (state, action: PayloadAction<CustomerInfo>) => {
            customerAdapter.updateOne(state.information.customers, {
                id: action.payload.id,
                changes: action.payload,
            });
        },
        changeInfoDate: (
            state,
            action: PayloadAction<{
                date: string;
                number: string;
            }>
        ) => {
            state.information.contract_number = action.payload.number;
            state.information.contract_date = action.payload.date;
        },

        removeCustomer: (state, action: PayloadAction<number>) => {
            customerAdapter.removeOne(state.information.customers, action.payload);
        },
        addVisualization: (state, action: PayloadAction<Visualization[]>) => {
            visualizationAdapter.addMany(state.visualization, action.payload);
        },

        removeVisualization: (state, action: PayloadAction<number>) => {
            visualizationAdapter.removeOne(state.visualization, action.payload);
        },

        removeProgress: (state, action: PayloadAction<number>) => {
            progressAdapter.removeOne(state.constructionProgress, action.payload);
        },

        editProgress: (state, action: PayloadAction<ConstructionProgress>) => {
            progressAdapter.updateOne(state.constructionProgress, {
                id: action.payload.id,
                changes: action.payload,
            });
        },

        onModalMode: (state: DashboardSchema, action: PayloadAction<string | null>) => {
            state.modalMode = action.payload;
        },
        changeIsEditedManager: (state: DashboardSchema, action: PayloadAction<boolean>) => {
            state.isEditedManager = action.payload;
        },
        changeCurrentCustomerId: (state: DashboardSchema, action: PayloadAction<number | null>) => {
            state.currentCustomerId = action.payload;
        },
        changeCurrentProgressId: (state: DashboardSchema, action: PayloadAction<number | null>) => {
            state.currentProgressId = action.payload;
            state.modalMode = 'constructionProgress';
        },
        changeCurrentManagerId: (state: DashboardSchema, action: PayloadAction<number | null>) => {
            state.currentManagerId = action.payload;
        },
        onCustomerModalLoader: (state: DashboardSchema, action: PayloadAction<boolean>) => {
            state.customerModalLoader = action.payload;
        },
        changeCurrentDocument: (state: DashboardSchema, action: PayloadAction<string | null>) => {
            state.documents.currentDocument = action.payload;
        },
        setCustomer: (state: DashboardSchema, { payload }: PayloadAction<number>) => {
            customerAdapter.updateOne(state.information.customers, {
                id: payload,
                changes: { selected: true },
            });
        },
        onModalLoader: (state: DashboardSchema, { payload }: PayloadAction<boolean>) => {
            state.modalLoader = payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getDashboard.pending, (state: DashboardSchema) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(
                getDashboard.fulfilled,
                (state: DashboardSchema, { payload }: PayloadAction<DashboardSchema>) => {
                    state.needFetchData = false;
                    state.isLoading = false;
                    state.error = null;
                    state.weather = payload.weather;
                    state.estimates = payload.estimates;
                    state.financial_info = { ...payload.financial_info };
                    state.can_show_financial_info = payload.can_show_financial_info;
                    state.can_export_financial_info = payload.can_export_financial_info;
                    state.is_can_archive_project = payload.is_can_archive_project;
                    state.is_customer = payload.is_customer;
                    state.tabs = payload.tabs;
                    state.is_owner = payload.is_owner;
                    state.information = { ...state.information, ...payload.information };
                    state.documents = { ...payload.documents, currentDocument: null };
                    state.albums = payload.albums;
                }
            )
            .addCase(getDashboard.rejected, (state: DashboardSchema, action) => {
                state.isLoading = false;
                state.error = action.error.message;
            })
            .addCase(deleteCustomer.pending, (state: DashboardSchema) => {
                state.editLoader = true;
                state.error = null;
            })
            .addCase(
                deleteCustomer.fulfilled,
                (state: DashboardSchema, { payload }: PayloadAction<number>) => {
                    state.editLoader = false;
                    state.error = null;
                    customerAdapter.updateOne(state.information.customers, {
                        id: payload,
                        changes: { selected: false },
                    });
                }
            )
            .addCase(deleteCustomer.rejected, (state: DashboardSchema, action) => {
                state.editLoader = false;
                state.error = action.error.message;
            })
            .addCase(editInfo.pending, (state: DashboardSchema) => {
                state.editLoader = true;
                state.error = null;
            })
            .addCase(
                editInfo.fulfilled,
                (
                    state: DashboardSchema,
                    { payload }: PayloadAction<UpdateInformationAboutObjectRequest>
                ) => {
                    state.editLoader = false;
                    state.error = null;
                    state.information.name = payload.name;
                    state.information.address = payload.address;
                    state.information.contract_date = payload.contract_date;
                    state.information.contract_number = payload.contract_number;
                    state.information.description = payload.description;
                    state.information.latitude = payload.coordinates.latitude;
                    state.information.longitude = payload.coordinates.longitude;
                }
            )
            .addCase(editInfo.rejected, (state: DashboardSchema, action) => {
                state.editLoader = false;
                state.error = action.error.message;
            })
            .addCase(addCustomer.pending, (state: DashboardSchema) => {
                state.customerModalLoader = true;
                state.error = null;
            })
            .addCase(
                addCustomer.fulfilled,
                (state: DashboardSchema, { payload }: PayloadAction<CustomerInfo>) => {
                    state.customerModalLoader = false;
                    state.error = null;
                    customerAdapter.addOne(state.information.customers, {
                        ...payload,
                        selected: true,
                    });
                }
            )
            .addCase(addCustomer.rejected, (state: DashboardSchema, action) => {
                state.customerModalLoader = false;
                state.error = action.error.message;
            })
            .addCase(editCustomer.pending, (state: DashboardSchema) => {
                state.customerModalLoader = true;
                state.error = null;
            })
            .addCase(
                editCustomer.fulfilled,
                (state: DashboardSchema, { payload }: PayloadAction<CustomerInfo>) => {
                    state.customerModalLoader = false;
                    state.error = null;
                    customerAdapter.updateOne(state.information.customers, {
                        id: payload.id,
                        changes: { name: payload.name },
                    });
                }
            )
            .addCase(editCustomer.rejected, (state: DashboardSchema, action) => {
                state.customerModalLoader = false;
                state.error = action.error.message;
            })
            .addCase(
                createManager.fulfilled,
                (state: DashboardSchema, { payload }: PayloadAction<ContactPerson>) => {
                    state.modalLoader = false;
                    state.error = null;
                    contactPersonsAdapter.addOne(state.information.contactPersons, { ...payload });
                }
            )
            .addCase(createManager.rejected, (state: DashboardSchema, action) => {
                state.modalLoader = false;
                state.error = action.error.message;
            })
            .addCase(createManager.pending, (state: DashboardSchema) => {
                state.modalLoader = true;
                state.error = null;
            })
            .addCase(
                editManager.fulfilled,
                (state: DashboardSchema, { payload }: PayloadAction<ContactPerson>) => {
                    state.modalLoader = false;
                    state.error = null;
                    contactPersonsAdapter.updateOne(state.information.contactPersons, {
                        id: payload.id,
                        changes: { ...payload },
                    });
                }
            )
            .addCase(editManager.pending, (state: DashboardSchema) => {
                state.modalLoader = true;
                state.error = null;
            })
            .addCase(editManager.rejected, (state: DashboardSchema, action) => {
                state.modalLoader = false;
                state.error = action.error.message;
            })
            .addCase(deleteManager.fulfilled, (state: DashboardSchema, { payload }) => {
                state.modalLoader = false;
                state.error = null;
                contactPersonsAdapter.removeOne(state.information.contactPersons, payload);
                state.modalMode = null;
            })
            .addCase(deleteManager.rejected, (state: DashboardSchema, action) => {
                state.modalLoader = false;
                state.error = action.error.message;
            })
            .addCase(deleteManager.pending, (state: DashboardSchema) => {
                state.modalLoader = true;
                state.error = null;
            })
            .addCase(uploadVisualization.fulfilled, (state: DashboardSchema, { payload }) => {
                state.modalLoader = false;
                state.error = null;
                visualizationAdapter.setAll(state.visualization, payload);
            })
            .addCase(uploadVisualization.rejected, (state: DashboardSchema, action) => {
                state.modalLoader = false;
                state.error = action.error.message;
            })
            .addCase(uploadVisualization.pending, (state: DashboardSchema) => {
                state.modalLoader = true;
                state.error = null;
            });
    },
});

// @ts-ignore
export const customersSelector = customerAdapter.getSelectors(
    // @ts-ignore
    (state) => state.dashboardReducer.information.customers
);

export const contactPersonsSelector = contactPersonsAdapter.getSelectors(
    // @ts-ignore
    (state) => state.dashboardReducer.information.contactPersons
);

export const managersSelector = projectManagerAdapter.getSelectors(
    // @ts-ignore
    (state) => state.dashboardReducer.information.projectUsers.project_users.manager
);

export const foremanSelector = projectForemanAdapter.getSelectors(
    // @ts-ignore
    (state) => state.dashboardReducer.information.projectUsers.project_users.foreman
);

export const purchaseSelector = projectPurchasesAdapter.getSelectors(
    // @ts-ignore
    (state) => state.dashboardReducer.information.projectUsers.project_users.purchase_agent
);
export const visualizationSelector = visualizationAdapter.getSelectors(
    // @ts-ignore
    (state) => state.dashboardReducer.visualization
);
export const progressSelector = progressAdapter.getSelectors(
    // @ts-ignore
    (state) => state.dashboardReducer.constructionProgress
);
export const { actions: dashboardActions } = dashboardSlice;
export const { reducer: dashboardReducer } = dashboardSlice;
